import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import type { RowSelectionState } from '@tanstack/react-table';
import { cn } from '@/lib/utils';
import type {
  BulkToolbarContext,
  DataTableProps,
  FilterValue,
  ResponseMeta,
  TableQueryState,
  TableRowData,
} from '../types';
import { Table } from '@/components/ui/table';
import { TableHeader } from './TableHeader';
import { TableBody } from './TableBody';
import { TableFooter } from './TableFooter';
import { DataTableToolbar } from './DataTableToolbar';
import { ErrorFallback } from '@/components/shared/placeholders/Error';
import { useDataTableQuery } from '../hooks/useDataTableQuery';
import { useDataTableState } from '../hooks/useDataTableState';
import { useDataTableInstance } from '../hooks/useDataTableInstance';
import { useDataTableStateActions } from '../hooks/useDataTableStateActions';
import { useDataTableActiveFilters } from '../hooks/useDataTableActiveFilters';
import { useDataTableStateEncoding } from '../hooks/useDataTableStateEncoding';
import { useDataTableLocalPreferences } from '../hooks/useDataTableLocalPreferences';
import { extractValidSortFields } from '../utils/statePersistence';
import { getLeafColumns } from '../utils/columnGroupUtils';
import { reconcileSelectionWithData } from '../utils/selectionReconcile';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  validateColumnsOperators,
  filterValidFilters,
  hydrateWithValidFilters,
} from '../utils/columnValidation';
import {
  DEFAULT_PAGE_SIZE_OPTIONS,
  DEFAULT_TABLE_FEATURES,
} from '../constants';
import { DataTableSearchContext } from '../DataTableSearchContext';

/**
 * All-in-one DataTable component with built-in query state management.
 * Use this for quick setup with server-side pagination and filtering.
 *
 * For more control, use DataTableStateProvider + useDataTableQuery + DataTable separately.
 */
function DataTableInner<
  TRow extends TableRowData,
  TMeta extends ResponseMeta = ResponseMeta,
>({
  id,
  columns,
  paginationOptions,
  initialState,
  noDataLabel,
  getQueryKeysFromParams,
  queryFn,
  queryConfig,
  onMeta,
  countScope,
  className,
  containerClassName,
  tableClassName,
  tableLayout = 'fixed',
  features,
  rowExpansion,
  onRowClick,
  getRowClassName,
  search,
  rowSelection: rowSelectionOptions,
}: DataTableProps<TRow, TMeta>) {
  const enableRowSelection = Boolean(rowSelectionOptions);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  // "Select all matching across pages" mode. While true, `rowSelection` is
  // repurposed as the EXCLUSION set (keys removed from the all-matching set).
  const [selectAllAcrossPages, setSelectAllAcrossPages] = useState(false);
  const pageSizeOptions =
    paginationOptions?.pageSizeOptions || DEFAULT_PAGE_SIZE_OPTIONS;
  const { state, dispatch } = useDataTableState({ initialState });
  const actions = useDataTableStateActions(dispatch, state.filters);
  const activeFilters = useDataTableActiveFilters(state.filters);
  // Columns flagged `hidden` still drive filtering / URL-state / persistence
  // (the full `columns` array below), but must not be rendered as a header or
  // body cell. `renderColumns` is the visible subset handed to the table
  // instance + header (KIR-3769 back-compat `type` filter behind `title`).
  const renderColumns = useMemo(
    () => columns.filter(column => !column.hidden),
    [columns]
  );
  const mergedFeatures = {
    ...DEFAULT_TABLE_FEATURES,
    ...features,
  };
  const enableRowExpansion = Boolean(mergedFeatures.enableRowExpansion);
  const enableSorting = mergedFeatures.enableSorting;
  const showToolbar = mergedFeatures.showToolbar;
  const showCardWrapper = mergedFeatures.showCardWrapper;
  const showShadow = mergedFeatures.showShadow;
  const enableLocalPreferences = mergedFeatures.enableLocalPreferences;
  const enableUrlState = mergedFeatures.enableUrlState;
  const debouncedSearch = useDebouncedValue(state.search, 300);
  const normalizedSearch = debouncedSearch.trim();
  const querySearch = normalizedSearch || undefined;

  const onSearchRef = useRef(search?.onSearch);
  useEffect(() => {
    onSearchRef.current = search?.onSearch;
  });
  useEffect(() => {
    onSearchRef.current?.(normalizedSearch);
  }, [normalizedSearch]);

  // Validate columns on mount - warn about invalid operator/dataType combinations
  useEffect(() => {
    validateColumnsOperators(columns);
  }, [columns]);

  // Wrap hydration callbacks to filter out invalid filters
  const handleHydrateFilters = useCallback(
    (filters: FilterValue[]) => {
      const validFilters = filterValidFilters(filters, columns);
      actions.setFilters(validFilters);
    },
    [columns, actions]
  );

  // localStorage hydration sets the whole state at once; drop invalid filters
  // (bad operator/column or non-[min,max] NUMBER range) so they can't 400 the table.
  const handleHydrateState = useCallback(
    (hydratedState: TableQueryState) => {
      actions.setState(hydrateWithValidFilters(hydratedState, columns));
    },
    [columns, actions]
  );

  const validSortFields = useMemo(
    () => extractValidSortFields(columns),
    [columns]
  );
  useDataTableLocalPreferences({
    tableId: id,
    state,
    onHydrateState: handleHydrateState,
    enabled: enableLocalPreferences,
    validSortFields,
  });
  useDataTableStateEncoding({
    tableId: id,
    filters: activeFilters,
    sort: state.sort,
    search: state.search,
    expandedRowIds: state.expandedRowIds,
    columns,
    onHydrateFilters: handleHydrateFilters,
    onHydrateSort: actions.setSort,
    onHydrateSearch: actions.setSearch,
    onHydrateExpanded: actions.setExpandedRowIds,
    enabled: enableUrlState,
  });

  const {
    data,
    pagination: paginationMeta,
    isLoading,
    isFetching,
    isError,
    error,
  } = useDataTableQuery<TRow, TMeta>({
    getQueryKeysFromParams,
    queryFn,
    queryConfig,
    onMeta,
    countScope,
    pagination: state.pagination,
    filters: activeFilters,
    sort: state.sort,
    search: querySearch,
  });
  // Deselect everything: exit "select all across pages" and drop the selection
  // (keys mode) or the exclusion set (select-all mode). Stable so it can back
  // both the bulk bar's "Clear Selection" AND the header checkbox in select-all
  // mode, where the header is a GLOBAL toggle rather than a per-page one.
  const clearSelection = useCallback(() => {
    setSelectAllAcrossPages(false);
    setRowSelection({});
  }, []);

  const table = useDataTableInstance<TRow>({
    data,
    columns: renderColumns,
    pagination: state.pagination,
    paginationMeta,
    enableRowSelection,
    selectAllAcrossPages,
    getRowId: rowSelectionOptions?.getRowId,
    rowSelection,
    onRowSelectionChange: setRowSelection,
    // In select-all mode the header checkbox spans ALL pages, so clicking it
    // clears the whole cross-page selection instead of only excluding the
    // current page (which left other pages selected — KIR-3252 bug).
    onSelectAllModeToggle: clearSelection,
  });

  // Full reset — a filter / search / external-token (e.g. the Alerts visibility
  // tab) change is a brand-new result set, so it invalidates ANY selection: both
  // a page-level (keys-mode) selection and a "select all matching across pages"
  // selection. Done during render via the "store previous key" pattern (React's
  // alternative to a setState-in-effect), guarded by `enableRowSelection`.
  const selectionResetToken = rowSelectionOptions?.resetToken;
  const selectionResetKey = useMemo(
    () => JSON.stringify([state.filters, querySearch, selectionResetToken]),
    [state.filters, querySearch, selectionResetToken]
  );
  const [prevSelectionResetKey, setPrevSelectionResetKey] =
    useState(selectionResetKey);
  const fullResetTriggered =
    enableRowSelection && prevSelectionResetKey !== selectionResetKey;
  if (fullResetTriggered) {
    setPrevSelectionResetKey(selectionResetKey);
    if (Object.keys(rowSelection).length > 0) {
      setRowSelection({});
    }
    if (selectAllAcrossPages) {
      setSelectAllAcrossPages(false);
    }
  }

  // Pagination clears ONLY a page-level (keys-mode) selection — per ticket FE#4
  // checkbox picks are per-page. "Select all matching across pages" is
  // query-scoped and spans pages by design (the user paginates to deselect rows
  // into the exclusion set, and the bar sends `excludeKeys`), so paginating must
  // NOT exit that mode or wipe its exclusions.
  const paginationKey = useMemo(
    () =>
      JSON.stringify([state.pagination.pageIndex, state.pagination.pageSize]),
    [state.pagination.pageIndex, state.pagination.pageSize]
  );
  const [prevPaginationKey, setPrevPaginationKey] = useState(paginationKey);
  const paginationChanged =
    enableRowSelection && prevPaginationKey !== paginationKey;
  if (paginationChanged) {
    setPrevPaginationKey(paginationKey);
    if (!selectAllAcrossPages && Object.keys(rowSelection).length > 0) {
      setRowSelection({});
    }
  }

  // Reconcile the selection against the freshly-fetched page: clear it over an
  // empty result set (e.g. the last matching alert was excluded out of view),
  // and prune orphaned keys a bulk action moved off the page so the bar's count
  // stays truthful. Pure logic lives in `reconcileSelectionWithData` (unit
  // tested); here we only apply the deltas it reports, keeping the render-time
  // update self-terminating (each field is `null` when unchanged).
  //
  // Skip the render where a reset just fired (full or pagination): the block(s)
  // above already adjusted the selection, and reconciling the pre-change
  // selection in the SAME render would fight that (the later `setRowSelection`
  // wins). The next render reconciles against the fetched data.
  const selectionKeyChanged = fullResetTriggered || paginationChanged;
  const reconcileGetRowId = rowSelectionOptions?.getRowId;
  const reconciled = selectionKeyChanged
    ? { rowSelection: null, selectAllAcrossPages: null }
    : reconcileSelectionWithData({
        enableRowSelection,
        isFetching,
        total: paginationMeta.total,
        selectAllAcrossPages,
        rowSelection,
        pageRowIds: reconcileGetRowId ? data.map(reconcileGetRowId) : [],
      });
  if (reconciled.selectAllAcrossPages !== null) {
    setSelectAllAcrossPages(reconciled.selectAllAcrossPages);
  }
  if (reconciled.rowSelection !== null) {
    setRowSelection(reconciled.rowSelection);
  }

  // Bulk-toolbar context: the engine's single egress for selection + query
  // state, derived directly from the controlled `rowSelection` (so the memo
  // recomputes as selection changes). Only assembled when selection is enabled.
  const bulkToolbar = rowSelectionOptions?.renderBulkToolbar;
  const getRowId = rowSelectionOptions?.getRowId;
  // Grouped columns carry their filters on the leaves, so flatten before
  // asking — a table whose only filterable fields sit under a group must still
  // offer "Clear filters".
  const hasFilterableColumns = useMemo(
    () => getLeafColumns(columns).some(column => column.filterable),
    [columns]
  );

  const bulkToolbarNode = useMemo(() => {
    if (!bulkToolbar || !getRowId) return null;
    const isMarked = (id: string): boolean => rowSelection[id] === true;
    const pageRowIds = data.map(getRowId);
    // `rowSelection` keys mean INCLUDED in keys mode, EXCLUDED in select-all mode.
    const markedIds = Object.keys(rowSelection).filter(id => rowSelection[id]);
    // Effectively-selected current-page rows (inverted in select-all mode).
    const selectedRows = data.filter(row => {
      const marked = isMarked(getRowId(row));
      return selectAllAcrossPages ? !marked : marked;
    });
    const allPageMarked = pageRowIds.length > 0 && pageRowIds.every(isMarked);
    const ctx: BulkToolbarContext<TRow> = {
      selectedRowIds: selectAllAcrossPages ? [] : markedIds,
      selectedRows,
      pageRowIds,
      clearSelection,
      selectAllAcrossPages,
      excludedRowIds: selectAllAcrossPages ? markedIds : [],
      enterSelectAllAcrossPages: () => {
        setSelectAllAcrossPages(true);
        setRowSelection({});
      },
      // Page-scoped header state (keys-mode semantics; the bar reads these only
      // outside select-all mode to decide whether to offer "select all").
      isAllPageSelected: allPageMarked,
      isSomePageSelected: pageRowIds.some(isMarked) && !allPageMarked,
      filters: activeFilters,
      search: querySearch ?? '',
      total: paginationMeta.total,
      pageCount: Math.ceil(paginationMeta.total / paginationMeta.limit) || 1,
      pageSize: state.pagination.pageSize,
    };
    return bulkToolbar(ctx);
  }, [
    bulkToolbar,
    getRowId,
    rowSelection,
    selectAllAcrossPages,
    clearSelection,
    data,
    activeFilters,
    querySearch,
    paginationMeta.total,
    paginationMeta.limit,
    state.pagination.pageSize,
  ]);

  return (
    <DataTableSearchContext.Provider value={normalizedSearch}>
      <div
        className={cn(
          'relative flex flex-col',
          showCardWrapper &&
            'rounded-md border border-table-border shadow-card overflow-hidden',
          !showCardWrapper && showShadow && 'shadow-card',
          className
        )}
      >
        {/* Bulk-action toolbar slot (KIR-3252): topmost, above the search toolbar
            per the ticket mockup. Renders nothing when no rows are selected. */}
        {bulkToolbarNode}
        {showToolbar && (
          <DataTableToolbar
            onClearFilters={actions.clearFilters}
            searchValue={state.search}
            onSearchChange={actions.setSearch}
            searchConfig={search}
            hasActiveFilters={activeFilters.length > 0}
            hasFilterableColumns={hasFilterableColumns}
          />
        )}
        <div
          className={cn(
            'relative isolate flex-1 overflow-auto',
            !showCardWrapper && 'rounded-md border',
            containerClassName
          )}
        >
          <Table
            addParentWrapper={false}
            className={cn(
              'min-w-full text-xs transition-opacity duration-200',
              tableLayout === 'auto' ? 'table-auto' : 'table-fixed',
              isFetching && 'opacity-50 pointer-events-none',
              tableClassName
            )}
          >
            {/* Authoritative column widths so grouped headers don't split a span
              50/50 under table-fixed. Omitted in `auto`, which sizes columns
              from their min/max instead. */}
            {tableLayout === 'fixed' && (
              <colgroup>
                {enableRowExpansion && <col className="w-10" />}
                {table.getVisibleLeafColumns().map(column => (
                  <col key={column.id} style={{ width: column.getSize() }} />
                ))}
              </colgroup>
            )}
            <TableHeader
              table={table}
              columns={renderColumns}
              filters={state.filters}
              onFilterChange={actions.setFilter}
              sort={state.sort}
              onSortChange={actions.toggleSort}
              enableSorting={enableSorting}
              enableRowExpansion={enableRowExpansion}
              tableLayout={tableLayout}
            />
            <TableBody
              table={table}
              isLoading={isLoading}
              isError={isError}
              error={error}
              noDataLabel={noDataLabel}
              enableRowExpansion={enableRowExpansion}
              rowExpansion={rowExpansion}
              onRowClick={onRowClick}
              getRowClassName={getRowClassName}
              searchValue={state.search}
              activeFilterCount={activeFilters.length}
              expandedRowIds={state.expandedRowIds}
              onToggleRowExpansion={actions.toggleRowExpansion}
              tableLayout={tableLayout}
            />
          </Table>
        </div>
        <TableFooter
          table={table}
          paginationState={state.pagination}
          pageSizeOptions={pageSizeOptions}
          onPageChange={actions.setPage}
          onPageSizeChange={actions.setPageSize}
        />
      </div>
    </DataTableSearchContext.Provider>
  );
}

export function DataTable<
  TRow extends TableRowData,
  TMeta extends ResponseMeta = ResponseMeta,
>({
  errorBoundaryFallback,
  errorBoundaryLabel,
  ...props
}: DataTableProps<TRow, TMeta>) {
  const fallbackElement = errorBoundaryFallback ?? (
    <ErrorFallback
      label={errorBoundaryLabel ?? 'Something went wrong loading the table.'}
    />
  );

  return (
    <Sentry.ErrorBoundary fallback={fallbackElement}>
      <DataTableInner<TRow, TMeta> {...props} />
    </Sentry.ErrorBoundary>
  );
}
